// @ts-check
import {reactive} from 'vue';
import {deepStrictEqual} from 'assert';

import {gmAPI} from '@/services/MapService';
import {throttle, wrapError} from '@northpoint-development/labs-common-core';
import {
  saveCompetitiveBuilding,
  saveMultiFamilyBuilding,
  saveCompetitiveBuildingComp,
  saveDataCenterBuilding,
  saveCompetitivePortfolio,
  saveCompetitiveOmBuilding,
  saveCompetitiveOmPortfolio,
  saveCompetitiveBulkBuilding,
  saveCompetitiveBulkPortfolio,
} from './helpers/competitive';

import {DisplayField} from './DisplayField';
import {Link} from '../Link';
import {saveFeature} from './helpers/feature';
import {THROTTLED_UPDATE_DELAY} from '@/constants/InfoWindowContent';
import {getStore} from '@/services/store';
import {getPresentation} from '@/services/ContentPresentation';

/**
 * @typedef {import('@/services/OverlaysService/Overlay').Overlay} Overlay
 * @typedef {import('@/services/GraphicService').ColorPickerOptions} ColorPickerOptions
 * @typedef {import('@/services/GraphicService').ToolbarOptions} ToolbarOptions
 *
 * @typedef {object} Comp
 * @property {string} type
 * @property {string} [group]
 * @property {Link} [link]
 * @property {DisplayField[]} displayFields
 * @property {Object.<string, any>} parent
 * @property {string} id
 * @property {string} name
 * @property {boolean} [generatedByOm]
 * @property {Record<string, unknown>} [underwritingData]
 *
 * @typedef {object} Toolbar
 * @property {ToolbarOptions[][]} toolbarOptions
 * @property {ColorPickerOptions[]} colorPickerOptions
 *
 * @typedef {object} Icon
 * @property {string} name
 * @property {string} color
 *
 * @callback hostStateFn A function for setting state on the object tied to the InfoWindowContent.
 * @param {boolean} state The new value of the state.
 * @returns {void}
 *
 * @callback hostActionFn A function to perform tasks on the object tied to the InfoWindowContent.
 * @returns {void}
 *
 * @callback hostAddToOverlayFn A function to add the object to an overlay.
 * @param {Overlay} overlay The overlay to add the object to.
 * @returns {Promise<void>}
 *
 * @typedef {object} Host Exposes information and methods on the object tied to the InfoWindowContent.
 * @property {boolean} [isTaxFeature]
 * @property {string} [firestoreCollection] Optional. If defined, denotes the Firestore collection that the object used
 * to create the InfoWindowContent belongs to.
 * @property {string} [filterCollection] Optional. If defined, denotes the local filter collection that the object used
 * to create the InfoWindowContent belongs to.
 * @property {string} [localCollection] Optional. If defined, denotes the local store collection that the object used
 * to create the InfoWindowContent belongs to.
 * @property {hostStateFn} [setHighlight] Function to set highlight state on the object tied to the InfoWindowContent.
 * @property {hostStateFn} [setSelected] Function to set selected state on the object tied to the InfoWindowContent.
 * @property {hostActionFn} [closeActions] Actions that should be performed when the InfoWindow is closed, or
 * the Tab containing the InfoWindowContent is removed.
 * @property {hostAddToOverlayFn} [addToOverlay] Action to add the object tied to the InfoWindowContent to an overlay.
 * @property {hostActionFn} [getEditModeToolbar]
 *
 * @typedef {object} InfoWindowContentData
 * @property {string} type
 * @property {string} title
 * @property {string|number|symbol} id
 * @property {google.maps.LatLng|google.maps.LatLngLiteral|{latitude:number,longitude:number}}[location]
 * @property {Object} metadata
 * @property {Link} [link]
 * @property {DisplayField[]} [displayFields]
 * @property {Comp[]} [comps]
 * @property {string} [selectedComp]
 * @property {Promise<Overlay[]>} [overlays]
 * @property {Toolbar} [toolbar]
 * @property {string} [featureType]
 * @property {Icon} [icon]
 * @property {Icon} [tabIcon]
 * @property {Host} [host]
 * @property {boolean} [editing]
 */

/** @type {Object.<string, (this: InfoWindowContent) => Promise<any>>} */
const databasePersistFunctions = {
  'competitiveBuilding': saveCompetitiveBuilding,
  'multiFamilyBuilding': saveMultiFamilyBuilding,
  'dataCenterBuilding': saveDataCenterBuilding,
  'competitiveBuildingComp': saveCompetitiveBuildingComp,
  'omCompetitiveBuilding': saveCompetitiveOmBuilding,
  'omCompetitivePortfolio': saveCompetitiveOmPortfolio,
  'bulkCompetitiveBuilding': saveCompetitiveBulkBuilding,
  'bulkCompetitivePortfolio': saveCompetitiveBulkPortfolio,
  'competitivePortfolio': saveCompetitivePortfolio,
  'feature': saveFeature,
};

/**
 * Which public collection/document a save persists, per type. Unmapped types (features, OM docs)
 * don't live in backend-filtered collections, so their saves skip the filter refresh.
 * @type {Object.<string, (content: InfoWindowContent) => {collection: string, doc: any} | null>}
 */
const backendFilterRefreshTargets = {
  'competitiveBuilding': (content) => ({collection: 'competitiveBuildings', doc: content}),
  'multiFamilyBuilding': (content) => ({collection: 'multiFamilyBuildings', doc: content}),
  'dataCenterBuilding': (content) => ({collection: 'dataCenterBuildings', doc: content}),
  'competitivePortfolio': (content) => ({collection: 'competitivePortfolios', doc: content}),
  // Comp saves persist the parent building document
  'competitiveBuildingComp': (content) => {
    const parent = content.comps?.find((comp) => comp.id === content.id)?.parent;
    return parent ? {collection: 'competitiveBuildings', doc: parent} : null;
  },
};

class InfoWindowContent {
  // Required properties
  /** @type {string} */
  type;
  /** @type {string | symbol} */
  id;
  /** @type {google.maps.LatLng|null} */
  location;
  /** @type {Object.<string, any>} */
  metadata;
  /** @type {Icon} */
  icon;
  /** @type {string} */
  shortName;

  // Optional properties
  /** @type {string|null} */
  title = null;
  /** @type {Link|null} */
  link = null;
  /** @type {DisplayField[]} */
  displayFields = [];
  /** @type {Comp[]} */
  comps = [];
  /** @type {string|null} */
  selectedComp = null;
  /** @type {Promise<Overlay[]>|null} */
  overlays = null;
  /** @type {Toolbar|null} */
  toolbar = null;
  /** @type {string|null} */
  featureType = null;
  /** @type {Host|null} */
  host = null;
  /** @type {boolean} Indicates if the InfoWindowContent is actively being edited */
  editing;

  // Internal properties
  /** @type {() => void} */
  _throttledUpdate;
  /** @type {(() => Promise<any>) | undefined} */
  _saveData;

  /**
   * InfoWindowContent captures all the relevant properties for an info window. Blocks will be displayed if they are
   * present and ignored otherwise. The type of info window is still required as some types may be displayed
   * differently even with the same fields.
   *
   * If an InfoWindowContent is initialized with another instance of InfoWindowContent, the new instance will be MOSTLY
   * a deep copy. The exceptions are the toolbar and Overlays, which will remain references to the original.
   * @param {InfoWindowContentData} contentData
   */
  constructor(contentData) {
    // Validate all required properties
    if (!contentData) {
      throw new Error('InfoWindowContent cannot be initialized with null or undefined');
    }
    if (!contentData.type || typeof contentData.type !== 'string') {
      throw new Error('InfoWindowContent must have a type');
    }
    this.type = contentData.type;

    this.icon = contentData.tabIcon ?? /** @type {Icon} */ ({});
    if (!this.icon.name) {
      Object.assign(this.icon, {name: this.getIconName(contentData)});
    }

    this.shortName = this.getShortName(contentData);

    if (contentData.id && typeof contentData.id === 'string') {
      this.id = contentData.id;
    } else if (typeof contentData.id === 'symbol') {
      this.id = contentData.id;
    } else if (typeof contentData.id === 'number') {
      this.id = contentData.id.toString();
    } else {
      throw new Error('InfoWindowContent must have an id');
    }

    this.location = null;
    // The union's members don't share accessors, so widen for the shape probing below
    const location = /** @type {any} */ (contentData?.location);
    if (location?.latitude && location?.longitude) {
      // Use != instead of !== because when the comparison is with 'null' specifically, it checks null and undefined
      // This will allow the latitude and longitude to be 0, 0
      this.location = new gmAPI.LatLng(location.latitude, location.longitude);
    } else if (location?.lat && location?.lng) { // LatLng or LatLngLiteral
      this.location = new gmAPI.LatLng(location);
    }

    if (!contentData.metadata || typeof contentData.metadata !== 'object') {
      throw new Error('InfoWindowContent must have metadata');
    }
    this.metadata = contentData.metadata;

    // Validate optional properties
    if (contentData.title) {
      if (typeof contentData.title !== 'string') {
        throw new Error('InfoWindowContent title must be a string');
      }
      this.title = contentData.title;
    }
    if (contentData.link) {
      if (!contentData.link.url || typeof contentData.link.url !== 'string') {
        throw new Error('InfoWindowContent link must have a url');
      }
      if (!contentData.link.text || typeof contentData.link.text !== 'string') {
        throw new Error('InfoWindowContent link must have text');
      }
      if (!contentData.link.type || typeof contentData.link.type !== 'string') {
        throw new Error('InfoWindowContent link must have a type');
      }
      this.link = new Link(contentData.link);
    }
    if (contentData.displayFields) {
      if (!Array.isArray(contentData.displayFields)) {
        throw new Error('InfoWindowContent displayFields must be an array');
      }
      this.displayFields = contentData.displayFields;
    }
    if (contentData.comps) {
      if (!Array.isArray(contentData.comps)) {
        throw new Error('InfoWindowContent comps must be an array');
      }
      this.comps = contentData.comps;
    }
    if (contentData.selectedComp) {
      if (typeof contentData.selectedComp !== 'string') {
        throw new Error('InfoWindowContent selectedComp must be a string');
      }
      this.selectedComp = contentData.selectedComp;
    }
    if (contentData.overlays) {
      // overlays is a promise, there isn't a good way to check that it is a promise
      this.overlays = contentData.overlays;
    }
    if (contentData.toolbar) {
      // check that toolbarOptions is an array
      if (!Array.isArray(contentData.toolbar.toolbarOptions)) {
        throw new Error('InfoWindowContent toolbar toolbarOptions must be an array');
      }
      this.toolbar = contentData.toolbar;
    }
    if (contentData.featureType) {
      this.featureType = contentData.featureType;
    }
    this.editing = contentData.editing ?? false;

    // The host object can look up the feature/item that the Info Window instance belongs to
    this.host = contentData.host ?? null;

    // We need to make this object reactive on creation so that Vue watchers will function correctly. If it is not made
    // reactive then any of the methods will refer to the original non-reactive target and not the reactive Proxy.
    const reactiveThis = reactive(this);

    // Compute internal properties
    // passing the update function to throttle() without binding it will cause the 'this' keyword to be undefined when
    // throttle() calls the function. The bound object must also be the reactive proxy, not the original object in order
    // for the Vue watchers to function correctly.
    this._throttledUpdate = throttle(this.updateSourceData.bind(reactiveThis), THROTTLED_UPDATE_DELAY);
    // Bind the saveData function to the correct databasePersistFunctions function, if it exists
    this._saveData = databasePersistFunctions[this.type]?.bind(reactiveThis);

    return reactiveThis;
  }

  /** True for OM/bulk staging review windows (not live competitive records). */
  get isStaged() {
    return (
      this.type === 'omCompetitiveBuilding' ||
      this.type === 'bulkCompetitiveBuilding' ||
      this.type === 'omCompetitivePortfolio' ||
      this.type === 'bulkCompetitivePortfolio'
    );
  }

  /**
   * Compares two InfoWindowContent instances to see if they are equal. This is a deep comparison, so all properties
   * are compared to the best extent possible.
   * @param {InfoWindowContent} other
   * @return {boolean}
   */
  equals(other) {
    if (!(other instanceof InfoWindowContent)) return false;
    if (this.type !== other.type) return false;
    if (this.title !== other.title) return false;
    if (this.id !== other.id) return false;
    if (this.location === null || other.location === null) {
      if (this.location !== other.location) return false;
    } else if (!this.location.equals(other.location)) {
      return false;
    }
    try {
      deepStrictEqual(this.link, other.link);
      deepStrictEqual(this.displayFields, other.displayFields);
      deepStrictEqual(this.comps, other.comps);
      deepStrictEqual(this.overlays, other.overlays);
    } catch {
      return false;
    }
    if (!Object.is(this.toolbar, other.toolbar)) return false;
    return true;
  }

  /**
   * Gets the index of a comp on this item by reference.
   * @param {Comp} comp The comp whose index to get.
   * @return {number} The index of the comp.
   */
  getCompIndex(comp) {
    return this.comps.findIndex((c) => c === comp);
  }

  /**
   * Sets the comps given an array of comps
   * @param {Comp[]} comps - the array of comps to set
   */
  setComps(comps) {
    this.comps = comps;
  }

  /**
   * Updates this item's name. If the name changes, schedules an update.
   * @param {string} newName The new name to set.
   */
  updateName(newName) {
    if (this.title !== newName) {
      this.title = newName;
      this._throttledUpdate();
    }
  }

  /**
   * Updates the name of a comp. If the name changes, schedules an update.
   * @param {Comp} comp The comp to update.
   * @param {string} newName The new name to set.
   */
  updateCompName(comp, newName) {
    const compIndex = this.getCompIndex(comp);
    if (this.comps[compIndex].name !== newName) {
      this.comps[compIndex].name = newName;
      this._throttledUpdate();
    }
  }

  /**
   * Adds a new DisplayField to this item and schedules an update.
   * @param {DisplayField} newField The field to add.
   * @param {number | null} [index]
   */
  addDisplayField(newField, index = null) {
    index = index ?? this.displayFields.length;

    this.displayFields.splice(index, 0, new DisplayField(newField));
    this._throttledUpdate();
  }

  /**
   * Updates a DisplayField on this item. If it changes, schedules an update.
   * @param {DisplayField} newField The updated value of the field.
   * @param {DisplayField} oldField The old value of the field.
   */
  updateDisplayField(newField, oldField) {
    if (!newField.equals(oldField)) {
      const index = this.displayFields.findIndex((df) => df.uuid === oldField.uuid);
      Object.assign(this.displayFields[index], newField);
      this._throttledUpdate();
    }
  }

  /**
   * Removes a DisplayField from this item and schedules an update.
   * @param {DisplayField} oldField The field to remove.
   */
  deleteDisplayField(oldField) {
    this.displayFields.splice(this.displayFields.indexOf(oldField), 1);
    this._throttledUpdate();
  }

  /**
   * Updates the parent of each of the comps for a competitive building
   */
  _updateCompParents() {
    // Whenever we update a parent, make sure all comp children have most recent parent data
    this.comps.forEach((comp) => {
      if (this.id === comp.parent?.id) {
        comp.parent = this;
      }
    });
  }

  /**
   * Adds a DisplayField to a comp on this item, then schedules an update.
   * @param {Comp} comp The comp to add to.
   * @param {DisplayField} newField The field to add.
   * @param {number | null} [index]
   */
  addCompDisplayField(comp, newField, index = null) {
    const compDisplayFields = this.comps[this.getCompIndex(comp)].displayFields;
    if (index === null) {
      // If no index provided, set the index to the length of the display fields array
      index = compDisplayFields.length;
    }

    compDisplayFields.splice(index, 0, newField);
    this._throttledUpdate();
  }

  /**
   * Updates a DisplayField on a comp on this item, then schedules an update if the value changed.
   * @param {Comp} comp The comp to update.
   * @param {DisplayField} newField The updated value of the field.
   * @param {DisplayField} oldField The old value of the field.
   */
  updateCompDisplayField(comp, newField, oldField) {
    if (newField.equals(oldField)) return;
    const childComp = this.comps[this.getCompIndex(comp)];
    let index = childComp.displayFields.findIndex((df) => df.uuid === oldField.uuid);
    if (index === -1) {
      throw new Error(`DisplayField with uuid ${oldField.uuid} not found on comp ${comp.name}`);
    }
    childComp.displayFields[index] = newField;
    this._throttledUpdate();
  }

  /**
   * Removes a DisplayField from a comp on this item, then schedules an update.
   * @param {Comp} comp The comp to remove from.
   * @param {DisplayField} oldField The field to remove.
   */
  deleteCompDisplayField(comp, oldField) {
    const childComp = this.comps[this.getCompIndex(comp)];
    childComp.displayFields = childComp.displayFields.filter((df) => df !== oldField);
    this._throttledUpdate();
  }

  /**
   * Adds a new comp to this item.
   * @param {Comp} comp The comp to add
   * @param {number | null} [index]
   */
  addComp(comp, index = null) {
    if (!comp.parent) {
      comp.parent = this; // Set the parent to the info window content if it doesn't already have it
    }
    index = index ?? this.comps.length;

    this.comps.splice(index, 0, comp);
    this._throttledUpdate();
  }

  /**
   * Removes a comp from this item.
   * @param {Comp} comp The comp to remove.
   */
  deleteComp(comp) {
    // Set the comps to a filtered list of the original without the deleted comp
    this.comps = this.comps.filter((c) => c !== comp);
    this._throttledUpdate();
  }

  async updateSourceData() {
    if (!this) {
      console.error('InfoWindowContent: updateSourceData called without binding to this');
      return;
    }
    if (!this._saveData) {
      console.warn(`InfoWindowContent: SAVE NOT IMPLEMENTED for type "${this.type}". If it were, a batched update would have just occurred.`);
      return;
    }
    if (this.comps?.length) {
      this._updateCompParents();
    }
    try {
      const result = await this._saveData();
      this._refreshBackendFilters();
      return result;
    } catch (err) {
      throw wrapError(err, `Error saving data for ${this.type} ${this.title} (${String(this.id)})`);
    }
  }

  /**
   * Re-runs active backend filters after a persisted edit so filter membership tracks the new
   * values. Fire-and-forget: the save doesn't wait on the refresh, and the action never rejects.
   */
  _refreshBackendFilters() {
    const target = backendFilterRefreshTargets[this.type]?.(this);
    if (!target) return;
    const editedAt = target.doc.metadata?.editedOn?.timestamp;
    const awaitDocs = editedAt instanceof Date
      ? [{collection: target.collection, id: target.doc.id, editedAtMillis: editedAt.getTime()}]
      : undefined;
    try {
      getStore().dispatch('filters/refreshBackendFilters', {awaitDocs});
    } catch (error) {
      // The save already succeeded; a refresh dispatch failure must not fail it
      console.error('Failed to dispatch backend filter refresh:', error);
    }
  }

  /**
   * Gets the icon name for the info window content
   * @param {InfoWindowContentData} data
   * @return {string} The icon name
   */
  getIconName(data) {
    const presentation = getPresentation(data.featureType);
    if (presentation) {
      return presentation.getIconName();
    }
    /** @type {Record<string, string>} */
    const iconNameMap = {
      'vectorTile': 'Layer-Icon',
      'tileLayer': 'Layer-Icon',
      'wmtsTile': 'Layer-Icon',
      'parcel': 'Layer-Icon',
      'taxOverlay': 'Layer-Icon',
      'overlay': 'Overlay-Icon',
      'default': '',
    };
    return iconNameMap[data.type] || iconNameMap.default;
  }

  /**
   * Gets the short name label for the info window content
   * @param {InfoWindowContentData} contentData
   * @return {string}
   */
  getShortName(contentData) {
    const {type, featureType} = contentData;
    const presentation = getPresentation(featureType);
    if (presentation) {
      return presentation.getShortName();
    }
    /** @type {Object.<string, string>} */
    const shortNameMap = {
      feature: 'Feature',
      industrialBuilding: 'Building',
      multiFamilyBuilding: 'Building',
      dataCenterBuilding: 'Building',
      competitiveBuilding: 'Comp Bldg',
      competitiveBuildingComp: 'Comp',
      industrialProject: 'Project',
      acquisitionProject: 'Project',
      vectorTile: 'Layer',
      taxOverlay: 'Overlay',
      overlay: 'Overlay',
      default: '',
    };
    return shortNameMap[type] || shortNameMap.default;
  }
}

export {InfoWindowContent};
